import { CSSProperties, FC, useState } from 'react';
import { Checkbox, Modal, Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { FinancialValueDisplayControl, useFinancialValueDisplay } from '@/shared/components/financial-values';
import DashedPane from '@/shared/components/layouts/sections/DashedPane';
import { preferenceModalBodyStyle } from '@/shared/components/modals/preferenceModalStyles';

import { OVERVIEW_SECTION_OPTIONS } from '../../configuration';
import { FinancialOverviewCustomizeModalProps, FinancialOverviewSectionKey } from '../../types';

export const FinancialOverviewCustomizeModal: FC<FinancialOverviewCustomizeModalProps> = ({
    configuration,
    financialValueDisplay,
    metricOptions,
    onChange,
    onClose,
    onFinancialValueDisplayChange,
    open,
    showSectionOptions = true,
    title = 'Customize Overview',
}) => {
    const { token } = useAntDesignTheme();
    const { preference: resolvedFinancialValueDisplay } = useFinancialValueDisplay();
    const [draft, setDraft] = useState(configuration);

    const optionCardStyle: CSSProperties = {
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusSM,
        padding: '0.625rem 0.75rem',
    };
    const optionGridStyle: CSSProperties = {
        display: 'grid',
        gap: 8,
        gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
    };
    const groupPaneProps = {
        height: 'auto' as const,
        marginTop: '0.25rem' as const,
        padding: '0.5rem' as const,
        style: {
            backgroundColor: token.colorBgBase,
            maxHeight: '14rem',
            minHeight: 0,
            overflowY: 'auto' as const,
        },
    };

    const toggleSection = (key: FinancialOverviewSectionKey, checked: boolean) => {
        setDraft((current) => ({
            ...current,
            sectionVisibility: {
                ...current.sectionVisibility,
                [key]: checked,
            },
        }));
    };

    const toggleMetric = (groupKey: 'detailMetricKeys' | 'summaryMetricKeys', metricKey: string, checked: boolean) => {
        setDraft((current) => {
            const currentKeys = current[groupKey];
            const nextKeys = checked
                ? Array.from(new Set([...currentKeys, metricKey]))
                : currentKeys.filter((key) => key !== metricKey);

            if (nextKeys.length === 0) {
                return current;
            }

            return {
                ...current,
                [groupKey]: nextKeys,
            };
        });
    };

    return (
        <Modal
            destroyOnClose
            okText='Apply'
            onCancel={onClose}
            onOk={() => {
                onChange(draft);
                onClose();
            }}
            open={open}
            styles={{
                body: {
                    ...preferenceModalBodyStyle,
                    maxHeight: 'calc(100vh - 12rem)',
                    overflow: 'hidden',
                },
            }}
            title={title}
            width={720}
        >
            <div style={{ display: 'grid', gap: '0.75rem', minHeight: 0 }}>
                {onFinancialValueDisplayChange ? (
                    <section data-testid='customize-financial-value-display'>
                        <Typography.Text style={{ color: token.colorTextTertiary }}>
                            Financial value display
                        </Typography.Text>
                        <DashedPane {...groupPaneProps}>
                            <FinancialValueDisplayControl
                                allowInherit
                                inheritLabel='Use account or workspace default'
                                resolvedValue={resolvedFinancialValueDisplay}
                                value={financialValueDisplay}
                                onChange={onFinancialValueDisplayChange}
                            />
                        </DashedPane>
                    </section>
                ) : null}
                {showSectionOptions ? (
                    <section data-testid='customize-statistics-sections'>
                        <Typography.Text style={{ color: token.colorTextTertiary }}>Sections</Typography.Text>
                        <DashedPane {...groupPaneProps}>
                            <div style={optionGridStyle}>
                                {OVERVIEW_SECTION_OPTIONS.map((option) => (
                                    <label key={option.key} style={optionCardStyle}>
                                        <Checkbox
                                            checked={draft.sectionVisibility[option.key]}
                                            onChange={(event) => toggleSection(option.key, event.target.checked)}
                                        >
                                            {option.label}
                                        </Checkbox>
                                    </label>
                                ))}
                            </div>
                        </DashedPane>
                    </section>
                ) : null}
                <section data-testid='customize-statistics-summary'>
                    <Typography.Text style={{ color: token.colorTextTertiary }}>Summary Metrics</Typography.Text>
                    <DashedPane {...groupPaneProps}>
                        <div style={optionGridStyle}>
                            {metricOptions.map((metric) => (
                                <label key={metric.key} style={optionCardStyle}>
                                    <Checkbox
                                        checked={draft.summaryMetricKeys.includes(metric.key)}
                                        onChange={(event) =>
                                            toggleMetric('summaryMetricKeys', metric.key, event.target.checked)
                                        }
                                    >
                                        {metric.label}
                                    </Checkbox>
                                </label>
                            ))}
                        </div>
                    </DashedPane>
                </section>
                <section data-testid='customize-statistics-detail'>
                    <Typography.Text style={{ color: token.colorTextTertiary }}>Detail Metrics</Typography.Text>
                    <DashedPane {...groupPaneProps}>
                        <div style={optionGridStyle}>
                            {metricOptions.map((metric) => (
                                <label key={metric.key} style={optionCardStyle}>
                                    <Checkbox
                                        checked={draft.detailMetricKeys.includes(metric.key)}
                                        onChange={(event) =>
                                            toggleMetric('detailMetricKeys', metric.key, event.target.checked)
                                        }
                                    >
                                        {metric.label}
                                    </Checkbox>
                                </label>
                            ))}
                        </div>
                    </DashedPane>
                </section>
            </div>
        </Modal>
    );
};
